import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileCode2, Loader2, Search, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { errMsg, todayIST } from '@/services/staffAttendanceApi';
import { ptApi } from '@/services/ptFilesApi';
import { PtInvoiceTable } from '@/components/ptFiles/PtInvoiceTable';
import { PtCodesDialog } from '@/components/ptFiles/PtCodesDialog';
import { PtDesignCodesDialog } from '@/components/ptFiles/PtDesignCodesDialog';
import { PtMailBar } from '@/components/ptFiles/PtMailBar';
import { PtCcDialog } from '@/components/ptFiles/PtCcDialog';
import { PtEmailDialog } from '@/components/ptFiles/PtEmailDialog';

const weekAgo = () => new Date(Date.now() - 6 * 864e5).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/** Shoper 'Goods Inward' (PT) file per shipped distributor invoice: Ready / Blocked with the gap, download, and the codes it needs. */
export default function PtFilesPage() {
  const navigate = useNavigate();
  const [f, setF] = useState({ date_from: weekAgo(), date_to: todayIST(), q: '' });
  const [rows, setRows] = useState(null);
  const [checks, setChecks] = useState({});
  const [denied, setDenied] = useState('');
  const [codesFor, setCodesFor] = useState(null);
  const [design, setDesign] = useState(null);
  const [gmail, setGmail] = useState(null);
  const [ccOpen, setCcOpen] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const loadGmail = () => ptApi.gmail().then(setGmail).catch(() => setGmail(null));
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('gmail') === 'connected') toast.success(`Gmail connected: ${p.get('email') || ''}`);
    if (p.get('gmail') === 'error') toast.error('Gmail wasn\'t connected', { description: p.get('reason') || '' });
    if (p.get('gmail')) window.history.replaceState(null, '', window.location.pathname);
    loadGmail();
  }, []);
  const run = useRef(0);
  const listRun = useRef(0);
  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const checkAll = useCallback(async (list) => {
    const id = ++run.current;
    setChecks({});
    for (let i = 0; i < list.length && id === run.current; i += 20) {
      const part = list.slice(i, i + 20).map((r) => r.invoice_no);
      const res = await ptApi.check(part).catch((e) => Object.fromEntries(part.map((n) => [n, { problems: [errMsg(e, 'Check failed')] }])));
      if (id === run.current) setChecks((c) => ({ ...c, ...res }));
    }
  }, []);
  const load = useCallback(() => {
    const id = ++listRun.current;
    setRows(null);
    ptApi.list(params()).then((r) => { if (id === listRun.current) { setRows(r); checkAll(r); } })
      .catch((e) => (e?.response?.status === 403 ? setDenied(errMsg(e, 'No access')) : toast.error(errMsg(e, 'Could not load the invoices'))));
  }, [JSON.stringify(f)]); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshRows = () => { const id = listRun.current; ptApi.list(params()).then((r) => id === listRun.current && setRows(r)).catch(() => {}); };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const recheck = () => rows && checkAll(rows);
  const vals = Object.values(checks);
  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="pt-files-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 h-16 flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" onClick={() => navigate('/eway-bills')} className="shrink-0 text-white hover:bg-white/10" data-testid="back-button"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-lg sm:text-xl font-bold whitespace-nowrap">PT Files</h1>
          <span className="hidden md:inline text-xs text-white/60">Shoper goods-inward file for each distributor invoice</span>
          {!denied && <Button variant="ghost" onClick={() => setDesign({ designs: [], hsn: [] })} className="ml-auto px-2 sm:px-4 text-white hover:bg-white/10" data-testid="pt-design-codes-btn">
            <FileCode2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Design Shoper codes</span></Button>}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {denied ? (
          <div className="text-center py-16 text-gray-500 space-y-2" data-testid="pt-no-access"><ShieldAlert className="w-10 h-10 mx-auto text-amber-500" /><p>{denied}</p></div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="w-[160px] bg-white" data-testid="pt-from" />
              <span className="text-sm text-gray-500">to</span>
              <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="w-[160px] bg-white" data-testid="pt-to" />
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Invoice or DC number" className="w-[240px] bg-white pl-9" data-testid="pt-search" />
              </div>
              {rows && <span className="text-sm text-gray-500" data-testid="pt-summary">{rows.length} invoice(s) · <b className="text-emerald-700">{vals.filter((v) => v.ready).length} ready</b> · <b className="text-rose-600">{vals.filter((v) => !v.ready).length} blocked</b></span>}
            </div>
            <PtMailBar gmail={gmail} onChanged={loadGmail} onCc={() => setCcOpen(true)} />
            {!rows ? <Loader2 className="w-6 h-6 mx-auto my-10 animate-spin text-amber-600" />
              : <PtInvoiceTable rows={rows} checks={checks} onCodes={setCodesFor} onDesigns={(c) => setDesign({ designs: c.missing_designs || [], hsn: c.missing_hsn || [] })}
                onDownloaded={refreshRows} onEmail={setEmailFor} canMail={!!gmail?.connected} />}
          </>
        )}
      </main>
      <PtCodesDialog customer={codesFor} onClose={() => setCodesFor(null)} onSaved={recheck} />
      <PtDesignCodesDialog missing={design} onClose={() => setDesign(null)} onSaved={recheck} />
      <PtCcDialog open={ccOpen} cc={gmail?.settings?.cc} onClose={() => setCcOpen(false)} onSaved={loadGmail} />
      <PtEmailDialog row={emailFor} gmail={gmail} onClose={() => setEmailFor(null)} onSent={refreshRows} />
    </div>
  );
}
